import { useState } from 'react'
import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { PageHeader } from '@/components/PageHeader'
import { IssueActions } from '@/components/issues/IssueActions'
import { ReportStatusBar } from '@/components/reports/ReportStatusBar'
import { UnpauseReportButton } from '@/components/reports/UnpauseReportButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  accountQuery,
  reportsOverviewQuery,
  subaccountsQuery,
  useCreateReport,
} from '@/lib/queries'
import { DEFAULT_STATUS_FILTER, reportStatusFilterSchema, splitByStatus } from '@/lib/status-filter'
import { useCanEditAccount } from '@/lib/use-account-permissions'

/**
 * A client workspace's Reports — the account's reports filtered to THIS client, since a report binds
 * to a sub-account. Creating here needs no client picker (the workspace IS the client); the row
 * opens the report's configuration, which lives at its account-scoped URL (the editor is a deep
 * surface shared by both workspaces). Gated on `delivery:reports` like the account routes, but a
 * disabled product redirects to the CLIENT Home — the workspace you're in, not the account above it.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/subaccounts/$subaccountId/reports')(
  {
    validateSearch: z.object({ status: reportStatusFilterSchema }),
    loader: async ({ context, params }) => {
      const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
      if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.reports)) {
        throw redirect({
          to: '/app/accounts/$accountId/subaccounts/$subaccountId',
          params: { accountId: params.accountId, subaccountId: params.subaccountId },
        })
      }
      await Promise.all([
        context.queryClient.ensureQueryData(reportsOverviewQuery),
        context.queryClient.ensureQueryData(subaccountsQuery(params.accountId)),
      ])
    },
    component: ClientReportsPage,
  },
)

function ClientReportsPage() {
  const { accountId, subaccountId } = Route.useParams()
  const navigate = useNavigate()
  const status = Route.useSearch().status ?? DEFAULT_STATUS_FILTER
  const { data: overview } = useSuspenseQuery(reportsOverviewQuery)
  const { data: subsData } = useSuspenseQuery(subaccountsQuery(accountId))
  const [creating, setCreating] = useState(false)
  // Creating writes to this sub-account, which an account:edit grant cascades into. A view-only
  // user sees the list but not the button — the API would 403 the create anyway.
  const canCreate = useCanEditAccount(accountId)

  const client = subsData.subaccounts.find((sub) => sub.id === subaccountId)
  const clientName = client?.name ?? 'this client'
  const { shown, counts } = splitByStatus(
    overview.reports.filter((report) => report.subaccountId === subaccountId),
    status,
  )
  const setStatus = (next: typeof status) =>
    void navigate({ to: '.', search: { status: next }, replace: true })

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageHeader
          title="Reports"
          description={`${client?.name ?? 'This client'}'s reports. Configure delivery, coverage and headers from a row; presentation lives with the account's template.`}
        />
        {canCreate && <Button onClick={() => setCreating(true)}>New report</Button>}
      </div>
      <ReportStatusBar
        status={status}
        counts={counts}
        isEmpty={shown.length === 0}
        emptyLabel={
          canCreate
            ? `No reports for ${clientName} yet. Create one with the button above.`
            : `No reports for ${clientName} yet.`
        }
        onChange={setStatus}
      />
      {shown.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Report</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Send time</th>
                <th className="px-3 py-2 text-left font-medium">Edit</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((report) => (
                <tr key={report.id} className="border-t border-border hover:bg-accent/30">
                  <td className="px-3 py-2">
                    {/* The name opens CONFIGURATION (what the report is); the Edit column is the
                        GENERATE side (today's draft, past issues) — same split as /app/reports. */}
                    <Link
                      to="/app/accounts/$accountId/reports/$reportId"
                      params={{ accountId, reportId: report.id }}
                      className="font-medium text-foreground underline-offset-4 hover:underline"
                    >
                      {report.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Badge variant={report.status === 'active' ? 'default' : 'secondary'}>
                        {report.status}
                      </Badge>
                      <UnpauseReportButton report={report} canEdit={canCreate} />
                    </div>
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    {report.sendTimes.join(', ')}
                    <span className="text-xs text-muted-foreground"> {report.schedule}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <IssueActions report={report} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <NewClientReportDialog
          accountId={accountId}
          subaccountId={subaccountId}
          clientName={client?.name ?? 'this client'}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  )
}

/** Name-only create: the workspace IS the client, so unlike the account page's dialog there is no
 *  client picker — the report lands in this sub-account and its configuration opens. */
function NewClientReportDialog({
  accountId,
  subaccountId,
  clientName,
  onClose,
}: {
  accountId: string
  subaccountId: string
  clientName: string
  onClose: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const createReport = useCreateReport(subaccountId, queryClient)

  return (
    <Dialog
      open
      onClose={onClose}
      title="New report"
      description={`Created in ${clientName}. Name it and its configuration opens.`}
      body={
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-foreground">Report name</span>
            <Input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Daily brief"
              aria-label="New report name"
            />
          </label>
          {createReport.isError && (
            <p role="alert" className="text-sm text-destructive">
              {createReport.error.message}
            </p>
          )}
        </div>
      }
    >
      <Button variant="outline" size="sm" onClick={onClose}>
        Cancel
      </Button>
      <Button
        size="sm"
        disabled={createReport.isPending || !name.trim()}
        onClick={() =>
          createReport.mutate(
            { name: name.trim() },
            {
              onSuccess: (report) => {
                // The overview query is keyed separately from the sub-account's own reports list,
                // so refresh it before leaving or the new row lags a navigation behind.
                void queryClient.invalidateQueries({ queryKey: ['reports-overview'] })
                void navigate({
                  to: '/app/accounts/$accountId/reports/$reportId',
                  params: { accountId, reportId: report.id },
                })
              },
            },
          )
        }
      >
        {createReport.isPending ? 'Creating…' : 'Create report'}
      </Button>
    </Dialog>
  )
}
